// src/components/CustomerSummary/components/ResizableTextTable.jsx
// ============================================================================
// BLOCK CS.UI.RTT.0 — ResizableTextTable (shared renderer)
// ----------------------------------------------------------------------------
// GOAL
// - One place for parsing + formatting + resizing across CustomerSummary tables.
// - Supports:
//     • 2-col and 3-col tab-delimited rows (auto-detect)
//     • Group header rows (line with only col1 populated)
//     • Nested/sub-rows (line with empty col1 and later cols populated)
//     • Group/block shading (PayerMix / Indications look)
//     • Column resizing + persistence
//
// INPUT
// - text: string (tab-delimited lines)
// - columnWidths: { col1, col2, col3? } (persisted externally)
// - onColumnWidthsChange(widths): persist handler
// - format: optional formatting profile:
//     {
//       headersByCols: {2:[..],3:[..]},
//       alignByCols: {2:[..],3:[..]},
//       defaultWidthsByCols: {2:{col1,col2},3:{col1,col2,col3}},
//       showHeaderRow: boolean,
//       showNoData: boolean,
//       enableNestedRows: boolean,
//       nestedIndentPx: number,
//       useGroupShading: boolean,
//       collapseRepeatedCol1: boolean,
//       multilineCols: number[] (1-based col indexes)
//     }
// ============================================================================

import React from 'react';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

// ============================================================================
// BLOCK CS.UI.RTT.1 — Helpers
// ============================================================================
function safeStr(v) {
  return v === null || v === undefined ? '' : String(v);
}

function trim0(v) {
  return safeStr(v)
    .replace(/\u00A0/g, ' ')
    .trim();
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function isFiniteNum(n) {
  return typeof n === 'number' && Number.isFinite(n);
}

function normalizeFormat(format) {
  const f = format || {};
  return {
    headersByCols: f.headersByCols || {},
    alignByCols: f.alignByCols || {},
    defaultWidthsByCols: f.defaultWidthsByCols || {},
    showHeaderRow: f.showHeaderRow !== false,
    showNoData: f.showNoData !== false,
    enableNestedRows: f.enableNestedRows !== false,
    nestedIndentPx: isFiniteNum(f.nestedIndentPx) ? f.nestedIndentPx : 18,
    useGroupShading: Boolean(f.useGroupShading),
    collapseRepeatedCol1: Boolean(f.collapseRepeatedCol1),
    multilineCols: Array.isArray(f.multilineCols) ? f.multilineCols : [],
  };
}

// ============================================================================
// BLOCK CS.UI.RTT.2 — Parse (auto 2/3 cols + group headers + nested rows)
// ----------------------------------------------------------------------------
// Row kinds:
// - header: col1 non-empty, col2/col3 empty
// - nested: col1 empty, some later col populated
// - row: normal data row
// ============================================================================
function detectCols(lines) {
  let maxParts = 0;
  lines.forEach((l) => {
    const parts = safeStr(l).split('\t');
    maxParts = Math.max(maxParts, parts.length);
  });
  return maxParts >= 3 ? 3 : 2;
}

function parseRows(text) {
  const rawLines = safeStr(text)
    .split(/\r?\n/)
    .map((l) => safeStr(l).replace(/\r/g, ''))
    .filter((l) => trim0(l).length > 0);

  const cols = detectCols(rawLines);
  let counter = 0;

  const rows = rawLines.map((line) => {
    counter += 1;
    const parts = line.split('\t');

    const c1 = trim0(parts[0] || '');
    const c2 = trim0(parts[1] || '');
    const c3 = trim0(parts[2] || '');

    const isHeader =
      c1.length > 0 && (cols === 2 ? c2.length === 0 : c2.length === 0 && c3.length === 0);

    if (isHeader) {
      return { kind: 'header', rowId: `hdr|${c1}|${counter}`, c1, c2: '', c3: '' };
    }

    const isNested =
      c1.length === 0 && (cols === 2 ? c2.length > 0 : c2.length > 0 || c3.length > 0);

    if (cols === 2) {
      return {
        kind: isNested ? 'nested' : 'row',
        rowId: `row2|${c1}|${c2}|${counter}`,
        c1,
        c2,
        c3: '',
      };
    }

    return {
      kind: isNested ? 'nested' : 'row',
      rowId: `row3|${c1}|${c2}|${c3}|${counter}`,
      c1,
      c2,
      c3,
    };
  });

  return { cols, rows };
}

// ============================================================================
// BLOCK CS.UI.RTT.3 — Resizing (pixel widths; supports right-edge resize)
// ----------------------------------------------------------------------------
// Notes
// - Widths are stored as pixels (sum = table width)
// - We allow dragging dividers between columns AND the far-right edge.
// ============================================================================
function parseWidthsForCols(cols, input) {
  const col1 = Number(input?.col1);
  const col2 = Number(input?.col2);
  const col3 = Number(input?.col3);

  if (cols === 2) {
    const ok = Number.isFinite(col1) && Number.isFinite(col2) && col1 > 0 && col2 > 0;
    return ok ? { col1, col2 } : null;
  }

  const ok3 =
    Number.isFinite(col1) &&
    Number.isFinite(col2) &&
    Number.isFinite(col3) &&
    col1 > 0 &&
    col2 > 0 &&
    col3 > 0;
  return ok3 ? { col1, col2, col3 } : null;
}

function defaultWidths(cols, format) {
  const byCols = format.defaultWidthsByCols || {};
  const f = byCols[cols];

  if (cols === 2 && f && isFiniteNum(f.col1) && isFiniteNum(f.col2)) {
    return { col1: f.col1, col2: f.col2 };
  }
  if (cols === 3 && f && isFiniteNum(f.col1) && isFiniteNum(f.col2) && isFiniteNum(f.col3)) {
    return { col1: f.col1, col2: f.col2, col3: f.col3 };
  }

  // sensible defaults (px)
  return cols === 2 ? { col1: 360, col2: 220 } : { col1: 360, col2: 260, col3: 160 };
}

function totalWidthPx(cols, widths) {
  if (!widths) {
    return 0;
  }
  if (cols === 2) {
    return Number(widths.col1) + Number(widths.col2);
  }
  return Number(widths.col1) + Number(widths.col2) + Number(widths.col3);
}

// ============================================================================
// BLOCK CS.UI.RTT.3.5 — ColumnDivider (drag handle; theme-aware via props)
// ============================================================================
function ColumnDivider({ onMouseDown, lineColor, hoverColor }) {
  return (
    <Box
      onMouseDown={onMouseDown}
      sx={{
        position: 'absolute',
        top: 0,
        right: -6,
        width: 12,
        height: '100%',
        cursor: 'col-resize',
        zIndex: 5,
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '1px',
          height: '100%',
          backgroundColor: lineColor,
        },
        '&:hover::after': {
          backgroundColor: hoverColor,
        },
      }}
    />
  );
}

// ============================================================================
// BLOCK CS.UI.RTT.4 — Component
// ============================================================================
export default function ResizableTextTable({ text, columnWidths, onColumnWidthsChange, format }) {
  const theme = useTheme();
  const fmt = React.useMemo(() => normalizeFormat(format), [format]);

  const { cols, rows } = React.useMemo(() => parseRows(text), [text]);

  const containerRef = React.useRef(null);

  // --------------------------------------------------------------------------
  // BLOCK CS.UI.RTT.4.A — Container width measurement (for % default)
  // --------------------------------------------------------------------------
  const [containerWidthPx, setContainerWidthPx] = React.useState(0);

  React.useEffect(() => {
    function measure() {
      const w = Number(containerRef.current?.offsetWidth) || 0;
      setContainerWidthPx(w);
    }

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // --------------------------------------------------------------------------
  // BLOCK CS.UI.RTT.4.B — Resolve widths (persisted first; else 80% container)
  // --------------------------------------------------------------------------
  const resolvedWidths = React.useMemo(() => {
    const parsed = parseWidthsForCols(cols, columnWidths);
    if (parsed) {
      return parsed;
    }

    if (!containerWidthPx) {
      return defaultWidths(cols, fmt);
    }

    const target = Math.floor(containerWidthPx * 0.8);

    const defaults = defaultWidths(cols, fmt);
    const totalDefault = totalWidthPx(cols, defaults);

    if (!totalDefault) {
      return defaults;
    }

    if (cols === 2) {
      const ratio1 = defaults.col1 / totalDefault;
      const col1 = Math.floor(target * ratio1);
      const col2 = target - col1;
      return { col1, col2 };
    }

    const ratio1 = defaults.col1 / totalDefault;
    const ratio2 = defaults.col2 / totalDefault;

    const col1 = Math.floor(target * ratio1);
    const col2 = Math.floor(target * ratio2);
    const col3 = target - col1 - col2;

    return { col1, col2, col3 };
  }, [cols, columnWidths, containerWidthPx, fmt]);

  const headers = React.useMemo(() => {
    const h = fmt.headersByCols?.[cols];
    if (Array.isArray(h) && h.length === cols) {
      return h;
    }
    return cols === 2 ? ['Item', 'Value'] : ['Item', 'Detail', 'Value'];
  }, [cols, fmt]);

  const align = React.useMemo(() => {
    const a = fmt.alignByCols?.[cols];
    if (Array.isArray(a) && a.length === cols) {
      return a;
    }
    return cols === 2 ? ['left', 'right'] : ['left', 'left', 'right'];
  }, [cols, fmt]);

  // --------------------------------------------------------------------------
  // BLOCK CS.UI.RTT.4.1 — Group shading assignment
  // --------------------------------------------------------------------------
  const rowsWithMeta = React.useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        if (r.kind === 'header') {
          const nextGroupIndex = acc.groupIndex + 1;
          const nextLastC1 = r.c1;
          return {
            groupIndex: nextGroupIndex,
            lastC1: nextLastC1,
            rows: [...acc.rows, { ...r, groupIndex: nextGroupIndex, lastC1: nextLastC1 }],
          };
        }

        const nextC1 = fmt.collapseRepeatedCol1 && r.c1 && r.c1 === acc.lastC1 ? '' : r.c1;
        const nextLastC1 = nextC1 || acc.lastC1;

        return {
          groupIndex: acc.groupIndex,
          lastC1: nextLastC1,
          rows: [...acc.rows, { ...r, c1: nextC1, groupIndex: acc.groupIndex, lastC1: nextLastC1 }],
        };
      },
      { groupIndex: -1, lastC1: '', rows: [] }
    ).rows;
  }, [rows, fmt.collapseRepeatedCol1]);

  // --------------------------------------------------------------------------
  // BLOCK CS.UI.RTT.4.2 — Resize handlers (px; includes far-right edge)
  // --------------------------------------------------------------------------
  const widthsRef = React.useRef(resolvedWidths);

  React.useEffect(() => {
    widthsRef.current = resolvedWidths;
  }, [resolvedWidths]);

  const dragRef = React.useRef(null);

  const persistWidths = React.useCallback(() => {
    if (typeof onColumnWidthsChange === 'function') {
      onColumnWidthsChange({ ...widthsRef.current });
    }
  }, [onColumnWidthsChange]);

  const onUpRef = React.useRef(null);

  const onMove = React.useCallback(
    (e) => {
      const state = dragRef.current;
      if (!state) {
        return;
      }

      const dx = e.clientX - state.startX;

      const minCol = 120;
      const maxCol = 1400;

      if (cols === 2) {
        if (state.which === 'c1') {
          const total = state.start.col1 + state.start.col2;
          const nextCol1 = clamp(state.start.col1 + dx, minCol, maxCol);
          const nextCol2 = clamp(total - nextCol1, minCol, maxCol);

          const fixedTotal = nextCol1 + nextCol2;
          const finalCol1 = clamp(nextCol1 + (total - fixedTotal), minCol, maxCol);

          const next = { col1: finalCol1, col2: total - finalCol1 };
          widthsRef.current = next;
          onColumnWidthsChange?.(next);
          return;
        }

        if (state.which === 'c2') {
          const nextCol2 = clamp(state.start.col2 + dx, minCol, maxCol);
          const next = { col1: state.start.col1, col2: nextCol2 };
          widthsRef.current = next;
          onColumnWidthsChange?.(next);
        }

        return;
      }

      if (state.which === 'c1') {
        const total = state.start.col1 + state.start.col2;
        const nextCol1 = clamp(state.start.col1 + dx, minCol, maxCol);
        const nextCol2 = clamp(total - nextCol1, minCol, maxCol);

        const fixedTotal = nextCol1 + nextCol2;
        const finalCol1 = clamp(nextCol1 + (total - fixedTotal), minCol, maxCol);

        const next = { col1: finalCol1, col2: total - finalCol1, col3: state.start.col3 };
        widthsRef.current = next;
        onColumnWidthsChange?.(next);
        return;
      }

      if (state.which === 'c2') {
        const total = state.start.col2 + state.start.col3;
        const nextCol2 = clamp(state.start.col2 + dx, minCol, maxCol);
        const nextCol3 = clamp(total - nextCol2, minCol, maxCol);

        const fixedTotal = nextCol2 + nextCol3;
        const finalCol2 = clamp(nextCol2 + (total - fixedTotal), minCol, maxCol);

        const next = { col1: state.start.col1, col2: finalCol2, col3: total - finalCol2 };
        widthsRef.current = next;
        onColumnWidthsChange?.(next);
        return;
      }

      if (state.which === 'c3') {
        const nextCol3 = clamp(state.start.col3 + dx, minCol, maxCol);
        const next = { col1: state.start.col1, col2: state.start.col2, col3: nextCol3 };
        widthsRef.current = next;
        onColumnWidthsChange?.(next);
      }
    },
    [cols, onColumnWidthsChange]
  );

  const onUp = React.useCallback(() => {
    dragRef.current = null;
    window.removeEventListener('mousemove', onMove);
    if (onUpRef.current) {
      window.removeEventListener('mouseup', onUpRef.current);
    }
    persistWidths();
  }, [onMove, persistWidths]);

  React.useEffect(() => {
    onUpRef.current = onUp;
  }, [onUp]);

  const startDrag = React.useCallback(
    (which) => (e) => {
      e.preventDefault();
      e.stopPropagation();

      dragRef.current = { which, startX: e.clientX, start: { ...resolvedWidths } };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [resolvedWidths, onMove, onUp]
  );

  // --------------------------------------------------------------------------
  // BLOCK CS.UI.RTT.4.3 — Cell style helpers (theme-aware)
  // --------------------------------------------------------------------------
  const cellSx = React.useCallback(
    (colIndex1Based, row) => {
      const isHeader = row.kind === 'header';
      const isNested = row.kind === 'nested' && fmt.enableNestedRows;

      const shaded = fmt.useGroupShading && row.groupIndex >= 0 ? row.groupIndex % 2 === 1 : false;
      const multiline = fmt.multilineCols.includes(colIndex1Based);

      const base = {
        fontSize: 13,
        paddingY: 0.75,
        paddingX: 1,
        verticalAlign: 'top',
        whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
        color: theme.palette.text.primary,
      };

      if (isHeader) {
        return {
          ...base,
          fontWeight: 700,
          backgroundColor: theme.palette.action.selected,
        };
      }

      if (shaded) {
        return {
          ...base,
          backgroundColor: theme.palette.action.hover,
        };
      }

      if (isNested && colIndex1Based === 2) {
        return { ...base, paddingLeft: `${fmt.nestedIndentPx}px` };
      }

      return base;
    },
    [fmt, theme]
  );

  const noData = rowsWithMeta.length === 0;
  const tableWidth = totalWidthPx(cols, resolvedWidths);

  const dividerLine = theme.palette.divider;
  const dividerHover = theme.palette.text.secondary;

  return (
    <Box ref={containerRef}>
      {noData && fmt.showNoData ? (
        <Typography
          variant="body2"
          sx={{ opacity: 0.7, paddingY: 1, color: theme.palette.text.secondary }}
        >
          No data
        </Typography>
      ) : null}

      {!noData ? (
        <Paper variant="outlined">
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table
              size="small"
              sx={{
                tableLayout: 'fixed',
                width: tableWidth,
                minWidth: tableWidth,
              }}
            >
              <colgroup>
                <col style={{ width: `${resolvedWidths.col1}px` }} />
                <col style={{ width: `${resolvedWidths.col2}px` }} />
                {cols === 3 ? <col style={{ width: `${resolvedWidths.col3}px` }} /> : null}
              </colgroup>

              {fmt.showHeaderRow ? (
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        width: `${resolvedWidths.col1}px`,
                        fontWeight: 700,
                        position: 'relative',
                        color: theme.palette.text.primary,
                      }}
                    >
                      {headers[0]}
                      <ColumnDivider
                        onMouseDown={startDrag('c1')}
                        lineColor={dividerLine}
                        hoverColor={dividerHover}
                      />
                    </TableCell>

                    <TableCell
                      align={align[1]}
                      sx={{
                        width: `${resolvedWidths.col2}px`,
                        fontWeight: 700,
                        position: 'relative',
                        color: theme.palette.text.primary,
                      }}
                    >
                      {headers[1]}

                      {cols === 3 ? (
                        <ColumnDivider
                          onMouseDown={startDrag('c2')}
                          lineColor={dividerLine}
                          hoverColor={dividerHover}
                        />
                      ) : null}

                      {cols === 2 ? (
                        <ColumnDivider
                          onMouseDown={startDrag('c2')}
                          lineColor={dividerLine}
                          hoverColor={dividerHover}
                        />
                      ) : null}
                    </TableCell>

                    {cols === 3 ? (
                      <TableCell
                        align={align[2]}
                        sx={{
                          width: `${resolvedWidths.col3}px`,
                          fontWeight: 700,
                          position: 'relative',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {headers[2]}
                        <ColumnDivider
                          onMouseDown={startDrag('c3')}
                          lineColor={dividerLine}
                          hoverColor={dividerHover}
                        />
                      </TableCell>
                    ) : null}
                  </TableRow>
                </TableHead>
              ) : null}

              <TableBody>
                {rowsWithMeta.map((r) => (
                  <TableRow key={r.rowId}>
                    <TableCell sx={cellSx(1, r)}>{r.c1}</TableCell>

                    <TableCell align={align[1]} sx={cellSx(2, r)}>
                      {r.c2}
                    </TableCell>

                    {cols === 3 ? (
                      <TableCell align={align[2]} sx={cellSx(3, r)}>
                        {r.c3}
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : null}
    </Box>
  );
}
